import React, { FC, useState } from 'react'
import { Box, Button, HStack, Spacer, useToggle } from '@revolut-internal/ui-kit'

import { useAssistantChatMessagesContext } from 'pages/AssistantPlayground/hooks'

import {
  chatInputMaxRows,
  chatInputWidth,
  proactiveInsightsPrompt,
} from './ChatInput.constants'
import { StyledBacking, StyledTextArea } from './ChatInput.styles'
import { TooltipButton } from './TooltipButton'

const isWhitespaceString = (str: string) => !str.replace(/\s/g, '').length

interface ChatInputProps {
  onCreateNewMessage: (message: string) => void
  submitDisabled?: boolean
}

export const ChatInput: FC<ChatInputProps> = ({
  onCreateNewMessage,
  submitDisabled = false,
}) => {
  const { setMemoryResetActive } = useAssistantChatMessagesContext()

  const [text, setText] = useState('')
  const [hasFocus, hasFocusToggle] = useToggle({ defaultState: true })

  const handleOnChange = (event: React.FormEvent<HTMLTextAreaElement>) => {
    const target = event.target as HTMLTextAreaElement
    setText(target.value)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      if (event.ctrlKey || event.metaKey || event.shiftKey) {
        event.preventDefault()
        setText(prev => `${prev}\n`)
      } else {
        event.preventDefault()
        if (!isWhitespaceString(text) && !submitDisabled) {
          handleSubmit()
        }
      }
    }
  }

  const handleSubmit = () => {
    onCreateNewMessage(text)
    setMemoryResetActive.off()
    setText('')
  }

  const handleProactiveInsightsClick = () => {
    onCreateNewMessage(proactiveInsightsPrompt)
  }

  const isSubmitDisabled = submitDisabled || isWhitespaceString(text)

  return (
    <Box width={chatInputWidth}>
      <StyledBacking data-focused={hasFocus}>
        <StyledTextArea
          aria-label="Chat input"
          placeholder="Type your message"
          value={text}
          onChange={handleOnChange}
          onKeyDown={handleKeyDown}
          maxRows={chatInputMaxRows}
          onFocus={hasFocusToggle.on}
          onBlur={hasFocusToggle.off}
          autoFocus
        />
        <Box p="s-8">
          <HStack space="s-8" align="center" pl="s-4">
            <TooltipButton
              title="Proactive insights"
              iconName="Cleaning"
              tooltipText="Proactive insights"
              disabled={submitDisabled}
              onClick={handleProactiveInsightsClick}
            />

            <Spacer />

            <Button
              variant="bar"
              useIcon="SendMessage"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
            >
              Send
            </Button>
          </HStack>
        </Box>
      </StyledBacking>
    </Box>
  )
}
